import type { TranslationTree } from '../../types';

export const labeling: TranslationTree = {
  labeling: {
    page: {
      breadcrumb: 'Étiquetage',
      title: "Étiquetage de l'état des outils",
      subtitle:
        "Classez chaque image d'arête selon l'état de l'outil pour entraîner le modèle de maintenance prédictive. Utilisez le clavier pour aller vite.",
    },
    shortcuts: {
      button: 'Raccourcis',
      title: 'Raccourcis clavier',
      labelOk: 'Étiqueter Bon état (OK)',
      labelWorn: 'Étiqueter Usé',
      classify: "Classifier avec l'IA",
      prevNext: 'Image précédente / suivante',
      undo: 'Annuler le dernier étiquetage',
    },
    toolbar: {
      modeFocus: 'Vue',
      modeGrid: 'Grille',
      modeDichotomous: 'Dichotomique',
      statusAll: 'Toutes',
      statusTodo: 'À faire',
      statusDone: 'Faites',
      progress: '{{done}} / {{total}} étiquetées',
      autoAdvance: 'Avance auto',
      modeLabel: "Mode d'étiquetage",
      statusLabel: 'Filtre par statut',
    },
    state: {
      ok: 'Bon état (OK)',
      worn: 'Usé',
      shortOk: 'OK',
      shortWorn: 'Usée',
      unreadable: 'Image illisible',
      shortUnreadable: 'Illisible',
    },
    panel: {
      stateHead: "État de l'outil",
      prev: 'Précédent',
      next: 'Suivant',
      undo: 'Annuler',
    },
    cnn: {
      button: "Classifier avec l'IA",
      loading: 'Classification…',
      suggestion: 'Suggestion IA : {{state}}',
      suggestionConfidence: 'Suggestion IA : {{state}} ({{confidence}} %)',
      accept: 'Accepter',
      reject: 'Rejeter',
      acceptAll: "Accepter tout l'IA",
      rejectAll: 'Tout rejeter',
      classifyAll: 'Tout',
      pendingCount: '{{count}} en attente',
      noScore: 'pas de score de confiance',
      tag: 'IA',
      error: 'Échec de la classification',
    },
    dev: {
      resetButton: 'Réinitialiser DEV',
      resetTitle: 'Réinitialiser les annotations',
      resetBody:
        'Efface tous les labels et suggestions IA de la session en cours. Développement uniquement.',
      resetConfirm: 'Réinitialiser',
      resetCancel: 'Annuler',
    },
    viewer: {
      imageAlt: "Image d'inspection de l'outil",
      zoomFit: 'Ajuster',
      zoomIn: 'Zoom avant',
      zoomOut: 'Zoom arrière',
      capturedAt: 'Capturé le {{datetime}}',
      imageCount: 'Image {{n}} sur {{total}}',
    },
    grid: {
      selectedCount: '{{count}} sélectionnée(s)',
      markAs: 'Marquer comme :',
      deselect: 'Désélectionner',
      bulkClassify: "Classifier avec l'IA",
      selectImage: "Sélectionner l'image",
      openImage: 'Ouvrir en mode Vue',
    },
    legend: {
      ok: 'Bon état',
      worn: 'Usé',
      unreadable: 'Illisible',
    },
    complete: {
      banner: 'Toutes les images de ce filtre sont étiquetées. Beau travail !',
    },
    selector: {
      title: 'Sélectionner un outil à étiqueter',
      subtitle: "Choisissez un outil pour démarrer ou continuer sa session d'étiquetage.",
      searchPlaceholder: 'Rechercher par outil ou référence',
      filterAll: 'Tous',
      filterInProgress: 'En cours',
      filterNotStarted: 'Non commencé',
      statusNotStarted: 'Non commencé',
      statusInProgress: 'En cours',
      statusComplete: 'Terminé',
      traceCount: '{{count}} traces',
      labeledCount: '{{done}} / {{total}} étiquetées',
      noReference: 'Aucune référence',
      startLabeling: "Démarrer l'étiquetage",
      cancelSelection: 'Annuler',
      selectedTool: 'Sélectionné : {{tool}}',
      changeTool: "Changer d'outil",
      emptyTitle: 'Aucun outil disponible',
      emptyHint: 'Aucun outil trouvé pour votre compte.',
      errorTitle: 'Impossible de charger les outils',
      errorRetry: 'Réessayer',
      loading: 'Chargement des outils…',
      experiments: 'Expériences',
      toolsIn: 'Pièces dans',
      partCount: 'pièces dans cette expérience',
      selectExperimentHint: 'Sélectionnez une expérience pour voir ses outils.',
    },
    export: {
      button: 'Exporter (Excel)',
      hint: 'Exporter les données étiquetées par tous les annotateurs : pour chaque outil étiqueté, le dernier bon état et le premier état usé (pièce et horodatage).',
      inProgress: 'Export… {{done}} / {{total}}',
      empty: 'Aucun outil étiqueté à exporter.',
      error: "Échec de l'export. Réessayez.",
    },
    partPicker: {
      title: 'Choisir une pièce',
      hint: "Choisissez la séquence d'images sur laquelle vous voulez travailler.",
      partLabel: 'Pièce {{partId}}',
      imageCount: '{{count}} images',
      fullyLabeled: 'Déjà étiquetée',
      empty: 'Aucune pièce disponible pour cette session.',
      allParts: 'Toutes les pièces',
      allPartsHint: 'Toutes les pièces, ordre chronologique mélangé.',
      triggerAll: 'Toutes les pièces',
      triggerSelected: 'Pièce {{partId}}',
    },
    dichotomous: {
      compare: {
        title: 'Comparaison',
        progress: 'Comparaison {{n}} sur ~{{total}}',
        question: 'Cette image est-elle encore en bon état, ou déjà usée ?',
        btnOk: 'Encore bon état',
        btnWorn: 'Usé',
        btnUnreadable: 'Image illisible',
        rangeLabel: 'Progression de la recherche dans la séquence',
        endpointFirst: 'Confirmez la première image : elle est attendue en bon état.',
        endpointLast: 'Confirmez la dernière image : elle est attendue usée.',
      },
      verify: {
        title: 'Vérification',
        progress: 'Vérification {{n}} sur {{total}}',
        question: 'Cette image est-elle encore en bon état, ou déjà usée ?',
        intro: 'Point de bascule trouvé. Étiquetez quelques images de plus pour le confirmer.',
      },
      conflict: {
        title: 'Résultat contesté',
        body: 'L’image {{position}} devait être « {{expected}} », mais vous l’avez étiquetée « {{given}} ».',
        expected: 'Attendu',
        given: 'Votre réponse',
        position: 'Image {{index}} sur {{total}}',
        actionRerun: 'Relancer la recherche à partir d’ici',
        actionKeep: 'Conserver ce point de bascule',
        overwriteWarning:
          'En conservant ce point de bascule, l’étiquette que vous venez de donner sera remplacée par l’étiquette du groupe lors de l’application.',
      },
      summary: {
        title: 'Prêt à appliquer',
        counts: '{{ok}} images seront étiquetées Bon état, {{worn}} seront étiquetées Usé.',
        confirm: 'Appliquer les étiquettes',
        cancel: 'Annuler',
        overwriteWarning: 'Les étiquettes existantes sur ces images seront remplacées.',
        unreadableCount: '{{count}} image(s) illisible(s) seront étiquetées Illisible.',
        restart: 'Relancer la recherche',
      },
      degenerate: {
        allWornTitle: 'Aucune image en bon état trouvée',
        allWornBody:
          'La première image est déjà usée : cette pièce ne montre aucune image en bon état dans cette série.',
        allOkTitle: 'Aucune image usée trouvée',
        allOkBody:
          'La dernière image est encore en bon état : cette pièce ne montre aucune usure dans cette série.',
        allUnreadableTitle: 'Aucune image lisible',
        allUnreadableBody:
          "Toutes les images de cette série sont illisibles : il n'y a aucun point de bascule à chercher.",
        applyAll: 'Étiqueter toute la série',
        fallbackManual: 'Étiqueter manuellement',
      },
      done: {
        title: 'Terminé',
        backToModes: "Retour aux modes d'étiquetage",
      },
    },
  },
};
